import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Coins, Users as UsersIcon, TrendingUp, TrendingDown, Wallet } from "lucide-react";

export default function Loyalty() {
  const [report, setReport] = useState(null);
  const [cfg, setCfg] = useState({ rand_spend_per_point: 10, rand_value_per_point: 1, earn_on_post_redemption_amount: true, require_customer_on_every_sale: false });
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(30);

  const load = async () => {
    try {
      const [rep, c] = await Promise.all([
        api.get(`/reports/loyalty`, { params: { days } }),
        api.get(`/loyalty/config`),
      ]);
      setReport(rep.data); setCfg(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load loyalty report");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/loyalty/config`, {
        rand_spend_per_point: Number(cfg.rand_spend_per_point),
        rand_value_per_point: Number(cfg.rand_value_per_point),
        earn_on_post_redemption_amount: !!cfg.earn_on_post_redemption_amount,
        require_customer_on_every_sale: !!cfg.require_customer_on_every_sale,
      });
      setCfg(r.data);
      toast.success("Loyalty configuration saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save configuration");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="loyalty-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl">Loyalty programme</h1>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Configuration • Reporting • Liability</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={UsersIcon} label="Customers" value={report?.total_customers ?? "—"} sub={`${report?.active_customers ?? 0} active`} testid="stat-total" />
        <StatCard icon={Coins} label="Outstanding points" value={report?.outstanding_points ?? "—"} sub={currency(report?.outstanding_liability_rand || 0)} testid="stat-outstanding" />
        <StatCard icon={TrendingUp} label={`Earned (${days}d)`} value={report?.points_earned_in_period ?? "—"} testid="stat-earned" />
        <StatCard icon={TrendingDown} label={`Redeemed (${days}d)`} value={report?.points_redeemed_in_period ?? "—"} testid="stat-redeemed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border p-5">
          <h2 className="font-display text-lg flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" strokeWidth={1.5} />Programme rules</h2>
          <p className="text-xs text-muted-foreground mt-1">Changes take effect immediately on future sales.</p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Rand spent per 1 point</label>
              <Input type="number" min="0.01" step="0.01" value={cfg.rand_spend_per_point}
                onChange={(e) => setCfg({ ...cfg, rand_spend_per_point: e.target.value })}
                data-testid="cfg-spend-per-point" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Rand redemption value per point</label>
              <Input type="number" min="0.01" step="0.01" value={cfg.rand_value_per_point}
                onChange={(e) => setCfg({ ...cfg, rand_value_per_point: e.target.value })}
                data-testid="cfg-value-per-point" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!cfg.earn_on_post_redemption_amount}
                onChange={(e) => setCfg({ ...cfg, earn_on_post_redemption_amount: e.target.checked })}
                data-testid="cfg-earn-post-redemption" />
              Earn points on amount paid <span className="text-muted-foreground">(after loyalty redemption)</span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!cfg.require_customer_on_every_sale}
                onChange={(e) => setCfg({ ...cfg, require_customer_on_every_sale: e.target.checked })}
                data-testid="cfg-require-customer" />
              Require a customer on every sale <span className="text-muted-foreground">(blocks walk-ins)</span>
            </label>
            <Button className="rounded-full bg-primary hover:bg-primary/90" disabled={saving} onClick={saveCfg} data-testid="cfg-save-btn">
              {saving ? "Saving…" : "Save configuration"}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg">Top members by spend</h2>
            <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value || "30", 10))))} className="w-24 h-8" data-testid="report-days" />
          </div>
          <div className="text-xs text-muted-foreground mt-1">Lifetime spend (period filter applies to earn/redeem stats above).</div>
          {(!report || report.top_customers_by_spend?.length === 0) ? (
            <div className="text-sm text-muted-foreground text-center py-8">No member sales yet.</div>
          ) : (
            <div className="mt-4 divide-y" data-testid="top-customers-list">
              {report.top_customers_by_spend.map((t) => (
                <Link key={t.customer_id} to={`/customers/${t.customer_id}`} className="flex items-center justify-between py-2 text-sm hover:bg-muted/40 rounded px-1" data-testid={`top-customer-${t.customer_id}`}>
                  <div>
                    <div className="font-medium">{t.name || t.customer_id}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{t.phone}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold">{currency(t.spend)}</div>
                    <div className="text-[10px] text-muted-foreground">{t.sales} txns</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
      </div>
      <div className="font-mono text-2xl tabular-nums font-semibold mt-1" data-testid={testid}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
